const PERMISSIONS_KEY = 'gk_permissions';
const DENIED_KEY = 'gk_denied_permissions';
const IS_GK_ADMIN_KEY = 'gk_is_admin';
const ACTIVE_MODULES_KEY = 'gk_active_modules';
const VOICE_ENABLED_KEY = 'gk_voice_enabled';
const SUPORTE_AVAILABLE_KEY = 'gk_suporte_available';

export const PERMISSIONS_UPDATED_EVENT = 'gk:permissions-updated';

export const NON_INHERITABLE_SUFFIXES = ['equipe', 'todos', 'proprio'] as const;

export function isNonInheritable(code: string): boolean {
  if (!code) return false;
  const lastSegment = code.split('.').pop();
  if (!lastSegment) return false;
  return (NON_INHERITABLE_SUFFIXES as readonly string[]).includes(lastSegment);
}

function notifyPermissionsUpdated() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(PERMISSIONS_UPDATED_EVENT));
}

export function savePermissions(
  permissions: string[],
  isGkAdmin: boolean,
  denied: string[] = [],
) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(permissions));
  localStorage.setItem(DENIED_KEY, JSON.stringify(denied));
  localStorage.setItem(IS_GK_ADMIN_KEY, JSON.stringify(isGkAdmin));
  notifyPermissionsUpdated();
}

export function getPermissions(): string[] {
  if (typeof window === 'undefined') return [];
  const raw = localStorage.getItem(PERMISSIONS_KEY);
  return raw ? JSON.parse(raw) : [];
}

export function getDeniedPermissions(): string[] {
  if (typeof window === 'undefined') return [];
  const raw = localStorage.getItem(DENIED_KEY);
  return raw ? JSON.parse(raw) : [];
}

export function getIsGkAdmin(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(IS_GK_ADMIN_KEY) === 'true';
}

export function saveModules(modules: string[]): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(ACTIVE_MODULES_KEY, JSON.stringify(modules));
  notifyPermissionsUpdated();
}

export function getActiveModules(): string[] {
  if (typeof window === 'undefined') return [];
  const raw = localStorage.getItem(ACTIVE_MODULES_KEY);
  return raw ? (JSON.parse(raw) as string[]) : [];
}

// Telefonia (voz) é SKU de RUNTIME, vem do backend (/auth/permissions →
// VOICE_ENABLED). Substitui a flag build-time NEXT_PUBLIC_VOICE_ENABLED — assim
// a imagem `gk-platform-web` base serve pra todos. SEM bypass de GK Admin: é
// config de deploy (módulo não existe no backend se off), não permissão.
export function saveVoiceEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(VOICE_ENABLED_KEY, JSON.stringify(enabled));
  notifyPermissionsUpdated();
}

export function getVoiceEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(VOICE_ENABLED_KEY) === 'true';
}

// Disponibilidade do módulo Suporte — vem do backend (/auth/permissions).
// Hoje só IXCSoft via banco direto tem o módulo; os demais ERPs e o modo API
// ainda estão em desenvolvimento. O layout do Suporte usa pra exibir o overlay.
export function saveSuporteAvailable(available: boolean): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(SUPORTE_AVAILABLE_KEY, JSON.stringify(available));
  notifyPermissionsUpdated();
}

export function getSuporteAvailable(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(SUPORTE_AVAILABLE_KEY) === 'true';
}

export function hasModule(name: string): boolean {
  if (getIsGkAdmin()) return true; // GK Admin enxerga tudo
  return getActiveModules().includes(name);
}

export function hasAnyPermission(prefix: string): boolean {
  if (getIsGkAdmin()) return true;
  const root = prefix.endsWith('.') ? prefix.slice(0, -1) : prefix;
  return getPermissions().some((p) => p === root || p.startsWith(`${root}.`));
}

export function clearPermissions() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(PERMISSIONS_KEY);
  localStorage.removeItem(DENIED_KEY);
  localStorage.removeItem(IS_GK_ADMIN_KEY);
  localStorage.removeItem(ACTIVE_MODULES_KEY);
  localStorage.removeItem(VOICE_ENABLED_KEY);
  localStorage.removeItem(SUPORTE_AVAILABLE_KEY);
  notifyPermissionsUpdated();
}

export function hasPermission(requiredCode: string): boolean {
  if (getIsGkAdmin()) return true;

  const grantedSet = new Set(getPermissions());
  const deniedSet = new Set(getDeniedPermissions());

  // 1. Veto exato
  if (deniedSet.has(requiredCode)) return false;

  // 2. Match exato
  if (grantedSet.has(requiredCode)) return true;

  // 3. Sufixo non-inheritable: sem climb
  if (isNonInheritable(requiredCode)) return false;

  // 4. Climb com veto em cada nivel
  const parts = requiredCode.split('.');
  for (let i = parts.length - 1; i > 0; i--) {
    const ancestor = parts.slice(0, i).join('.');
    if (deniedSet.has(ancestor)) return false;
    if (grantedSet.has(ancestor)) return true;
  }

  return false;
}
